import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { API_CONFIG, getApiUrl } from '../lib/config';
import { supabase } from '../lib/supabase/client';
import { getGamesList, completeOnboarding, getOnboardingStatus } from '../lib/api/userApi';

const OnboardingPage = () => {
  const { user } = useAuth();
  const { resetCheck, setHasCompletedOnboarding } = useOnboarding();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    preferredName: '',
    gender: '',
    email: '',
    age: '',
    region: '',
    gameId: '',
    gameName: '',
    experience: '',
    otherGame: ''
  });
  
  // Games list
  const [games, setGames] = useState([]);
  const [fetchingGames, setFetchingGames] = useState(false);
  
  // User metadata from JWT
  const [userMetadata, setUserMetadata] = useState(null);
  
  // Form section validation states
  const [isBasicInfoValid, setIsBasicInfoValid] = useState(false);
  const [isPreferencesValid, setIsPreferencesValid] = useState(false);
  const [isGameInfoValid, setIsGameInfoValid] = useState(false);
  
  // Define the form fields structure
  const formSections = [
    {
      id: 'basic-info',
      title: 'Basic Information',
      fields: [
        { id: 'firstName', title: 'First Name', description: 'What is your first name?', type: 'text' },
        { id: 'lastName', title: 'Last Name', description: 'What is your last name?', type: 'text' },
        { id: 'age', title: 'Age', description: 'What is your age?', type: 'number', min: 13, max: 120 },
        { id: 'email', title: 'Email', description: 'Your email address', type: 'email', disabled: true }
      ]
    },
    {
      id: 'preferences',
      title: 'Preferences',
      fields: [
        { 
          id: 'preferredName', 
          title: 'Preferred Name', 
          description: 'What name would you like us to call you?', 
          type: 'text' 
        },
        { 
          id: 'gender', 
          title: 'Gender', 
          description: 'What is your gender?', 
          type: 'select',
          options: [
            { id: 0, text: 'Male' },
            { id: 1, text: 'Female' },
            { id: 2, text: 'Other' },
            { id: 3, text: 'Prefer not to say' }
          ]
        },
        { 
          id: 'region', 
          title: 'Region', 
          description: 'Which region are you from?', 
          type: 'select',
          options: [
            { id: 'NA', text: 'North America' },
            { id: 'EU', text: 'Europe' },
            { id: 'AS', text: 'Asia' },
            { id: 'SA', text: 'South America' },
            { id: 'OC', text: 'Oceania' },
            { id: 'AF', text: 'Africa' },
            { id: 'Other', text: 'Other' }
          ]
        }
      ]
    },
    {
      id: 'game-info',
      title: 'Game Information',
      fields: [
        { 
          id: 'gameId', 
          title: 'Game', 
          description: 'What game/sport do you play?',
          type: 'game-select'
        },
        { 
          id: 'experience', 
          title: 'Experience', 
          description: 'How would you describe your experience level?',
          type: 'select',
          options: [
            { id: 0, text: 'Beginner' },
            { id: 1, text: 'Intermediate' },
            { id: 2, text: 'Advanced' },
            { id: 3, text: 'Professional' }
          ]
        }
      ]
    }
  ];

  // Get user metadata and games list
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user) {
        navigate('/signin');
        return;
      }
      
      setLoading(true);
      
      try {
        // Get the user's metadata from Supabase auth
        const { data: userData } = await supabase.auth.getUser();
        const metadata = userData?.user?.user_metadata || {};
        setUserMetadata(metadata);
        
        // Pre-fill data from metadata
        setFormData(prev => ({
          ...prev,
          firstName: metadata.first_name || '',
          lastName: metadata.last_name || '',
          age: metadata.age ? metadata.age.toString() : '',
          email: userData?.user?.email || '',
          preferredName: metadata.preferred_name || '',
          gender: metadata.gender !== undefined ? metadata.gender : '',
          region: metadata.region || ''
        }));
        
        // Fetch games list
        setFetchingGames(true);
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        
        if (!accessToken) {
          throw new Error('No access token available');
        }
        
        const response = await fetch(getApiUrl('/api/v1/user/list-games'), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch games: ${response.status}`);
        }
        
        const gamesData = await response.json();
        
        // Filter out system games (ID 999)
        const userGames = gamesData.filter(game => game.gameId !== 999);
        setGames(userGames);
        
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Failed to load necessary data. Please try again.');
      } finally {
        setLoading(false);
        setFetchingGames(false);
      }
    };
    
    fetchInitialData();
  }, [user, navigate]);
  
  // Validate sections whenever form data changes
  useEffect(() => {
    // Validate basic info section
    const isBasicValid = 
      formData.firstName.trim() !== '' && 
      formData.lastName.trim() !== '' && 
      formData.age !== '' && 
      parseInt(formData.age) >= 13;
    
    setIsBasicInfoValid(isBasicValid);
    
    // Validate preferences section
    const isPrefsValid = 
      formData.preferredName.trim() !== '' && 
      formData.gender !== '' && 
      formData.region !== '';
    
    setIsPreferencesValid(isPrefsValid);
    
    // Validate game info section
    let isGameValid = formData.experience !== '';
    
    if (formData.gameId === 999) {
      // If "Other" game is selected, require otherGame field
      isGameValid = isGameValid && formData.otherGame.trim() !== '';
    } else {
      // Otherwise require a gameId
      isGameValid = isGameValid && formData.gameId !== '';
    }
    
    setIsGameInfoValid(isGameValid);
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectOption = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleGameSelect = (gameId, gameName) => {
    if (gameId === 999) {
      // "Other" option - show text input for custom game
      setFormData(prev => ({ 
        ...prev, 
        gameId: 999,
        gameName: "SYSTEM_OTHER",
        otherGame: ''
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        gameId,
        gameName,
        otherGame: '' // Clear other game if a specific game is selected
      }));
    }
  };

  const isFormValid = () => {
    return isBasicInfoValid && isPreferencesValid && isGameInfoValid;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setError('Please complete all required fields.');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      // Get session token for authorization
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      
      if (!accessToken) {
        setError('You are not logged in. Please sign in and try again.');
        setTimeout(() => navigate('/signin'), 2000);
        return;
      }
      
      // Build onboarding data
      const onboardingData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        preferredName: formData.preferredName || formData.firstName,
        email: formData.email,
        gender: formData.gender,
        age: parseInt(formData.age),
        region: formData.region,
        gameId: parseInt(formData.gameId),
        experience: parseInt(formData.experience),
        otherGame: formData.otherGame
      };
      
      console.log('Submitting onboarding data:', onboardingData);
      
      // Use the completeOnboarding function from userApi
      const { data, error: apiError } = await completeOnboarding(onboardingData);
      
      if (apiError) {
        throw new Error(`API error: ${apiError.message || 'Failed to complete onboarding'}`);
      }
      
      // Success
      console.log('Onboarding completed successfully');
      
      // Update onboarding context and redirect to home
      setHasCompletedOnboarding(true);
      resetCheck();
      
      // Navigate to the main app
      window.location.href = '/';
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setError('Failed to complete the onboarding process. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4 py-10">
      <div className="w-full max-w-3xl p-8 bg-white rounded-lg shadow-md">
        <div className="mb-8 text-center">
          <img 
            src="/images/penguin-logo.svg" 
            alt="Pokket Logo" 
            className="w-16 h-16 mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to Pokket</h2>
          <p className="text-lg text-gray-600">
            Please complete this onboarding form to get started. We'll use this information to personalize your experience.
          </p>
          
          {error && (
            <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          {formSections.map((section) => (
            <div key={section.id} className="p-6 border border-gray-200 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">{section.title}</h3>
              
              <div className="space-y-6">
                {section.fields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <label htmlFor={field.id} className="block text-lg font-medium text-gray-700">
                      {field.description}
                    </label>
                    
                    {/* Text/Number input fields */}
                    {(field.type === 'text' || field.type === 'number' || field.type === 'email') && (
                      <input
                        type={field.type}
                        id={field.id}
                        name={field.id}
                        value={formData[field.id]}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder={`Enter your ${field.title.toLowerCase()}`}
                        min={field.min}
                        max={field.max}
                        disabled={field.disabled}
                        required
                      />
                    )}
                    
                    {/* Option selection */}
                    {field.type === 'select' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {field.options.map((option) => (
                          <div 
                            key={option.id}
                            onClick={() => handleSelectOption(field.id, option.id)}
                            className={`p-4 rounded-lg cursor-pointer transition-all ${
                              formData[field.id] === option.id
                                ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                                : 'card-shadow hover:shadow-[0_0_10px_rgba(59,130,246,0.5)] hover:scale-105'
                            }`}
                          >
                            <h4 className="text-lg font-medium text-gray-800">{option.text}</h4>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Game selection */}
                    {field.type === 'game-select' && (
                      <div>
                        {fetchingGames ? (
                          <div className="flex justify-center p-4">
                            <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                              {games.map((game) => (
                                <div 
                                  key={game.gameId}
                                  onClick={() => handleGameSelect(game.gameId, game.gameName)}
                                  className={`p-4 rounded-lg cursor-pointer transition-all ${
                                    formData.gameId === game.gameId
                                      ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                                      : 'card-shadow hover:shadow-[0_0_10px_rgba(59,130,246,0.5)] hover:scale-105'
                                  }`}
                                >
                                  <h4 className="text-lg font-medium text-gray-800">{game.gameName}</h4>
                                </div>
                              ))}
                              
                              {/* Other option */}
                              <div 
                                onClick={() => handleGameSelect(999, "SYSTEM_OTHER")}
                                className={`p-4 rounded-lg cursor-pointer transition-all ${
                                  formData.gameId === 999
                                    ? 'bg-blue-50 shadow-[0_0_10px_rgba(59,130,246,0.5)]' 
                                    : 'card-shadow hover:shadow-[0_0_10px_rgba(59,130,246,0.5)] hover:scale-105'
                                }`}
                              >
                                <h4 className="text-lg font-medium text-gray-800">Other</h4>
                              </div>
                            </div>
                            
                            {/* Custom game input */}
                            {formData.gameId === 999 && (
                              <input
                                type="text"
                                name="otherGame"
                                value={formData.otherGame}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 mt-4"
                                placeholder="Enter your game/sport"
                                required
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Section validation indicator */}
              <div className="mt-4 text-right">
                {section.id === 'basic-info' && isBasicInfoValid && (
                  <span className="text-green-500 text-sm flex items-center justify-end">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Complete
                  </span>
                )}
                {section.id === 'preferences' && isPreferencesValid && (
                  <span className="text-green-500 text-sm flex items-center justify-end">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Complete
                  </span>
                )}
                {section.id === 'game-info' && isGameInfoValid && (
                  <span className="text-green-500 text-sm flex items-center justify-end">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Complete
                  </span>
                )}
              </div>
            </div>
          ))}
          
          <div className="text-center mt-8">
            <button
              type="submit"
              disabled={!isFormValid() || submitting}
              className={`px-8 py-3 text-lg font-medium rounded-md transition-colors ${
                !isFormValid() || submitting
                  ? 'bg-blue-300 text-white cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {submitting ? (
                <>
                  <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                  Completing...
                </>
              ) : 'Complete Onboarding'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnboardingPage; 